import PropTypes from "prop-types";
import { useCallback, useState } from "react";
import { connect } from "react-redux";
import { HTTP_STATUS_CODES, PAYMENT_COUPON_TYPE, PROMO_CODE_STATUS, STRATEGY } from "app/constants";
import { bindActionCreators } from "redux";
import PromotionCode, { PROMOTION_STATE } from "app/pages/Booking/PromotionCode/PromotionCode";
import { sendTagOnVoucherCodeSubmission } from "app/utils/analytics";
import {
	clearPromotionValue,
	sendPromotionCode,
} from "app/pages/Booking/PromotionCode/promotionCodeActionCreators";
import { logout } from "app/pages/Auth/AuthActions";
import { useNavigate } from "react-router-dom";
import { Formik } from "formik";
import { useIntl } from "react-intl";
import {
	clearStopoverPromotionValue,
	sendStopoverPromotionCode,
} from "app/pages/Stopover/stopoverActionCreators";
import { useAnalytics } from "app/utils/analytics/useAnalytics";

const PromotionCodeContainer = props => {
	const {
		sendPromotionCode,
		sendStopoverPromotionCode,
		quotationCode,
		logout,
		strategy,
		status,
		onValidPromoCodeApplied,
		onFocus,
		couponType,
		clearPromotionValue,
		clearStopoverPromotionValue,
		promotionCode,
		promotionValue,
		isStopover,
		disabled,
		userBiid,
	} = props;

	const [isSubmitting, setIsSubmitting] = useState(false);
	const navigate = useNavigate();
	const { track } = useAnalytics();

	const intl = useIntl();

	const initialValues = {
		promoCode: promotionCode,
	};

	const isPromoCodeActive = couponType === PAYMENT_COUPON_TYPE.PROMOCODE;

	const isValidPromocodeApplied =
		isPromoCodeActive && (status === PROMO_CODE_STATUS.SUCCESS || promotionValue > 0);

	const isUnvalidPromocodeApplied =
		isPromoCodeActive && status === PROMO_CODE_STATUS.INVALID_PROMOCODE;

	const sendCodePromo = useCallback(
		(values, formikBag) => {
			setIsSubmitting(true);

			const requestValues = { ...values };

			sendTagOnVoucherCodeSubmission();

			const requestData = { ...requestValues, code: quotationCode, userBiid: userBiid };

			const sendPromotionCodeFunction = isStopover
				? sendStopoverPromotionCode
				: sendPromotionCode;

			sendPromotionCodeFunction(requestData).then(res => {
				setIsSubmitting(false);

				track("promotion_code_apply", {
					httpCode: res?.status,
					status: res?.data?.status,
					promoCode: values?.promoCode,
				});

				if (res.status === HTTP_STATUS_CODES.OK) {
					if (
						((res.data.status === PROMO_CODE_STATUS.SUCCESS && values.promoCode) ||
							res.data.status === PROMO_CODE_STATUS.REMOVED_PROMOCODE) &&
						typeof onValidPromoCodeApplied === "function"
					) {
						onValidPromoCodeApplied(values.promoCode);
					} else if (res.data.status === PROMO_CODE_STATUS.INVALID_PROMOCODE) {
						formikBag.setFieldError(
							"promoCode",
							intl.formatMessage({ id: "promotion.code.error.message" })
						);
						// error message is not shown here but is mandatory to trigger the error state
					}
				} else if (res.status === HTTP_STATUS_CODES.FORBIDDEN) {
					logout();
					navigate({ pathname: "/home/login" }, { replace: true });

					if (strategy !== STRATEGY.OAUTH) {
						navigate("/home/login");
					} else {
						navigate("/technical-error/sso");
					}
				}
			});
		},
		[
			status,
			quotationCode,
			sendPromotionCode,
			onValidPromoCodeApplied,
			logout,
			isStopover,
			navigate,
			strategy,
		]
	);

	const handleFormSubmit = useCallback((values, formikBag) => {
		return sendCodePromo(values, formikBag);
	}, []);

	const onClearPromotionInput = useCallback(() => {
		const clearPromotionCodefuntion = isStopover
			? clearStopoverPromotionValue
			: clearPromotionValue;

		clearPromotionCodefuntion({ promoCode: "", code: quotationCode });
	}, [quotationCode, isStopover]);

	let promocodeState = PROMOTION_STATE.DEFAULT;

	if (isValidPromocodeApplied) {
		promocodeState = PROMOTION_STATE.SUCCESS;
	} else if (isUnvalidPromocodeApplied) {
		promocodeState = PROMOTION_STATE.ERROR;
	}

	return (
		<Formik initialValues={initialValues} onSubmit={handleFormSubmit} enableReinitialize>
			{({ handleSubmit, values }) => {
				const isFieldEmpty = values.promoCode === "";

				return (
					<PromotionCode
						onFocus={onFocus}
						handleSubmit={handleSubmit}
						promocodeState={promocodeState}
						disableButton={Boolean(disabled || isSubmitting || isFieldEmpty)}
						disableField={Boolean(disabled)}
						onClearInput={onClearPromotionInput}
					/>
				);
			}}
		</Formik>
	);
};

PromotionCodeContainer.propTypes = {
	status: PropTypes.string,
	sendPromotionCode: PropTypes.func,
	sendStopoverPromotionCode: PropTypes.func,
	logout: PropTypes.func,
	quotationCode: PropTypes.string,
	strategy: PropTypes.string,
	onValidPromoCodeApplied: PropTypes.func,
	onFocus: PropTypes.func,
	clearPromotionValue: PropTypes.func,
	clearStopoverPromotionValue: PropTypes.func,
	couponType: PropTypes.string,
	promotionCode: PropTypes.string,
	promotionValue: PropTypes.number,
	isStopover: PropTypes.bool,
	disabled: PropTypes.bool,
	userBiid: PropTypes.string,
};

const mapStateToProps = state => {
	return {
		quotationCode: state.quotation.code,
		disabled: state.booking.credits > 0,
		status: state.promotion.status,
		promotionValue: state.promotion.promotionValue || 0,
		promotionCode: state.promotion.promotionCode || "",
		strategy: state.partner.strategy,
		couponType: state.booking.coupon,
		userBiid: state.auth.userBiid,
	};
};

const mapDispatchToProps = dispatch => {
	return bindActionCreators(
		{
			sendPromotionCode,
			sendStopoverPromotionCode,
			clearPromotionValue,
			clearStopoverPromotionValue,
			logout,
		},
		dispatch
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(PromotionCodeContainer);
